import React, { useEffect, useRef, useMemo, useCallback } from "react";

import { HighlighterTool } from "@treegram/tools-highlighter";

import SpaceTypo from "@/models/enums/space-typo.ts";
import ValidationPack from "../../../models/validation-pack.ts";
import useValidationContext from "../../../context/validation/use-validation-context.ts";

import { ValidationMode } from "../types/validation-mode-type.ts";
import useViewerItemsColorContext from "@/services/context/viewer/items-color/use-viewer-items-color-context.ts";
import useViewerItemsSelectionContext from "@/services/context/viewer/items-selection/use-viewer-items-selection-context.ts";
import ThermicTypo from "@/models/enums/thermic-typo.ts";

import { AssociatedObjectAndTypo } from "../models/associated-object-and-typo.ts";
import ViewerCanvas from "@/services/context/viewer/instance/viewer-canvas.tsx";
import useViewerContext from "@/services/context/viewer/instance/use-viewer-context.ts";

interface PackViewerContainerProps extends React.PropsWithChildren {
    pack: ValidationPack;
    mode: ValidationMode;
}

const PackViewerContainer: React.FC<PackViewerContainerProps> = ({ pack, mode }) => {

    const { isLoading, viewer, getTool } = useViewerContext();
    const { packsData } = useValidationContext();
    const { subscribeColorChanged } = useViewerItemsColorContext();
    const { addItem, subscribeSelectionChanged } = useViewerItemsSelectionContext();

    const modeRef = useRef(mode);

    const packObjectTypos = useMemo(() => packsData.get(pack.id)?.objectTypos ?? [], [packsData, pack.id]);

    const packObjectTyposRef = useRef<AssociatedObjectAndTypo[]>(packObjectTypos);

    useEffect(() => {
        modeRef.current = mode;
    }, [mode]);

    useEffect(() => {
        packObjectTyposRef.current = packObjectTypos;
    }, [packObjectTypos]);

    const handleGeometriesSelected = useCallback((ids: string[]) => {
        // Sélection
        if (ids.length > 0) {
            ids.forEach(id => {
                const match = packObjectTyposRef.current.find(pot => pot._3D === id);
                if (match) {
                    addItem(match.object);
                }
            });
        }

    }, [addItem]);

    useEffect(() => {
        const highlighter = getTool(HighlighterTool);
        if (isLoading || viewer == null || highlighter == null) return () => { /* */ };

        const setupHighlights = async () => {
            const changeColor: Promise<void>[] = [];
            packObjectTyposRef.current.forEach(pot => {
                if (modeRef.current === "thermal") {
                    changeColor.push(highlighter.highlightItemsStyle(`Thermic-${ThermicTypo[pot.thermalTypo]}`, [pot._3D!]));
                }
                else {
                    changeColor.push(highlighter.highlightItemsStyle(`Space-${SpaceTypo[pot.shellTypo]}`, [pot._3D!]));
                }
            });

            await Promise.all(changeColor);
            await viewer.showItems(packObjectTyposRef.current.map(pot => pot._3D!));
        };

        setupHighlights();

        return () => { /* */ };
    }, [getTool, isLoading, viewer]);

    /* --------------- MISE EN EVIDENCE --------------- */

    useEffect(() => {
        const highlighter = getTool(HighlighterTool);
        if (highlighter == null) return () => { /* Ignored */ };
        highlighter.onItemsSelected.add(handleGeometriesSelected);

        return () => {
            highlighter.onItemsSelected.remove(handleGeometriesSelected);
        };
    }, [getTool, handleGeometriesSelected, isLoading]);

    useEffect(() => 
        subscribeSelectionChanged(async event => {
            if (event.action === "clear") {
                const highlighter = getTool(HighlighterTool);
                if (highlighter != null) {
                    await highlighter.clearStyle("select");
                }
            }
        }), [getTool, subscribeSelectionChanged]);

    useEffect(() => subscribeColorChanged(async event => {
        const highlighter = getTool(HighlighterTool);
        if (viewer == null || highlighter == null || event.items.length === 0) return () => { /* Ignored */ };

        const clearColor: Promise<void>[] = [];
        const changeColor: Promise<void>[] = [];
        
        event.items.forEach(item => {
            const pot = packObjectTyposRef.current.find(_pot => _pot.object.workspaceUid === item.workspaceUid && _pot.object.objectUid === item.objectUid);
            if (!pot) return;

            if (modeRef.current === "thermal") {
                clearColor.push(highlighter.clearStyle(`Thermic-${ThermicTypo[pot.thermalTypo]}`, [pot._3D!]));
                pot.thermalTypo = event.thermalTypo!;
                changeColor.push(highlighter.highlightItemsStyle(`Thermic-${ThermicTypo[event.thermalTypo!]}`, [pot._3D!]));
            }
            else {
                clearColor.push(highlighter.clearStyle(`Space-${SpaceTypo[pot.shellTypo]}`, [pot._3D!]));
                pot.shellTypo = event.shellTypo!;
                changeColor.push(highlighter.highlightItemsStyle(`Space-${SpaceTypo[event.shellTypo!]}`, [pot._3D!]));
            }

            packObjectTyposRef.current = [...packObjectTyposRef.current.filter(m => m._3D !== pot._3D), pot];
        });

        await Promise.all(clearColor);
        await Promise.all(changeColor);

        return () => { /* */ };
    }), [getTool, subscribeColorChanged, viewer]);

    useEffect(() => {
        const highlighter = getTool(HighlighterTool);
        if (isLoading || viewer == null || highlighter == null) return () => { /* */ };

        const changeMode = async () => {
            const clearColor: Promise<void>[] = [];
            const changeColor: Promise<void>[] = [];

            packObjectTyposRef.current.forEach(pot => {
                if (mode === "thermal") {
                    Object.values(SpaceTypo)
                        .filter(value => typeof value === "string").forEach((value) => clearColor.push(highlighter.clearStyle(`Space-${value}`, [pot._3D!])));
                    changeColor.push(highlighter.highlightItemsStyle(`Thermic-${ThermicTypo[pot.thermalTypo]}`, [pot._3D!]));
                }
                else {
                    Object.values(ThermicTypo)
                        .filter(value => typeof value === "string").forEach((value) => clearColor.push(highlighter.clearStyle(`Thermic-${value}`, [pot._3D!])));
                    changeColor.push(highlighter.highlightItemsStyle(`Space-${SpaceTypo[pot.shellTypo]}`, [pot._3D!]));
                }
            });

            await Promise.all(clearColor);
            await Promise.all(changeColor);
        };

        changeMode();

        return () => { /* */ };
    }, [getTool, isLoading, mode, viewer]);

    return (
        <ViewerCanvas />
    );
};

export default PackViewerContainer;
